import MainModel from "../../../components/Models/main-model";
import { brandPalette } from "../../../constants/brandPalette";
import {
  Badge,
  Box,
  FormLabel,
  Heading,
  Textarea,
  Button,
  VStack,
  HStack,
  Text,
} from "@chakra-ui/react";
import { useSelector, useDispatch } from "react-redux";
import { closeAdminHomeworkCommentModel } from "../../../modules/admin-modules/models/Actions";
import { useState, useEffect } from "react";
import { commentStuentHomeworkRequest } from "../../../modules/admin-modules/reports/Actions";
import { getAssistantsRequest } from "../../../modules/admin-modules/teachersAssistant/Actions";
import Select from "react-select";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../components/Models/admin-modal-theme";
import { homeworkSubmissionPdfUrl } from "../../../components/student/PdfPreviewModal";
import HomeworkPdfAnnotator from "./homework-pdf-annotator";

const modalSelectStyles = {
  ...adminSelectStyles(),
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
};

const HOMEWORK_GRADES = ["A*", "A", "B", "Incomplete"];

const textareaProps = {
  bg: "var(--surface-muted)",
  color: "var(--text-primary)",
  borderColor: "var(--border-subtle)",
  borderRadius: "14px",
  _placeholder: { color: "var(--text-muted)", opacity: 1 },
  _focus: {
    borderColor: brandPalette.primary,
    boxShadow: "0 0 0 3px rgba(91, 13, 22, 0.12)",
  },
};

const HomeworkComment = ({ data, homeworkId }) => {
  const openHomeworkComment = useSelector((state) => state.model.isOpen27);
  const isLoading = useSelector((state) => state.reports.isLoading);
  const [comment, setComment] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const dispatch = useDispatch();
  const assistants = useSelector((state) => state.teacherassistants.assistants);
  const [assistant, setAssistant] = useState("");
  const [assistantComment, setAssistantComment] = useState("");
  const [annotatedFile, setAnnotatedFile] = useState(null);
  const [annotatorOpen, setAnnotatorOpen] = useState(false);
  const assistantsOptions = assistants?.data?.map((data) => {
    return { value: data.id, label: data.name, sign: data.name };
  });

  const pdfUrl = data?.file ? homeworkSubmissionPdfUrl(data.file) : "";

  useEffect(() => {
    dispatch(getAssistantsRequest());
  }, [dispatch]);

  const resetForm = () => {
    setComment("");
    setSelectedGrade("");
    setAssistant("");
    setAssistantComment("");
    setAnnotatedFile(null);
    setAnnotatorOpen(false);
  };

  const applyGrade = (grade) => {
    setSelectedGrade(grade);
    setComment((prev) => {
      const reviewedByMatch = prev.match(/\n\n Reviewed by: .+$/);
      const reviewedBy = reviewedByMatch ? reviewedByMatch[0] : "";
      // If the selected grade is "Incomplete", use a different message
      if (grade === "Incomplete") {
        return `Your homework is accepted but it is incomplete.${reviewedBy}`;
      }
      return `The grade for your homework is: ${grade}${reviewedBy}`;
    });
  };

  const header = (
    <Box>
      <Heading textAlign="left" color="var(--text-primary)" fontSize="20px" fontWeight="900">
        Review submission
      </Heading>
      <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
        Add evaluation, annotate the PDF, and save assistant notes.
      </Text>
    </Box>
  );

  const handelAddComment = () => {
    const formData = new FormData();
    formData.append("homework_id", homeworkId);
    formData.append("student_id", data?.student?.id);
    formData.append("evaluation", comment);
    if (assistant) formData.append("assistant_id", assistant);
    if (assistantComment) formData.append("assistant_comment", assistantComment);
    if (annotatedFile) formData.append("file", annotatedFile);

    dispatch(
      commentStuentHomeworkRequest({
        id: data?.id,
        formData,
        action: () => {
          dispatch(closeAdminHomeworkCommentModel());
          resetForm();
        },
      })
    );
  };

  const closeFunc = () => {
    dispatch(closeAdminHomeworkCommentModel());
    resetForm();
  };

  return (
    <>
      <MainModel
        isLoading={isLoading}
        header={header}
        closeFunc={closeFunc}
        openVar={openHomeworkComment}
        buttonTitle={"Save changes"}
        buttonFunc={handelAddComment}
        cancelTitle="Cancel"
        contentProps={adminModalContentProps}
        headerProps={adminModalHeaderProps}
        bodyProps={adminModalBodyProps}
        footerProps={{
          borderTop: "1px solid",
          borderColor: "var(--border-subtle)",
          py: 6,
        }}
      >
        <VStack align="stretch" spacing={5} dir="ltr">
          <HStack justify="space-between" flexWrap="wrap" spacing={3}>
            <Text fontSize="sm" fontWeight="800" color="var(--text-primary)">
              Student:{" "}
              <Text as="span" fontWeight="900">
                {data?.student?.name || "N/A"}
              </Text>
            </Text>
            <HStack spacing={2} flexWrap="wrap">
              {data?.file ? (
                <>
                  <Button
                    as="a"
                    href={pdfUrl}
                    target="_blank"
                    size="sm"
                    borderRadius="12px"
                    bg="var(--surface-muted)"
                    border="1px solid"
                    borderColor="var(--border-subtle)"
                    color="var(--text-primary)"
                    fontWeight="800"
                    _hover={{ bg: "var(--surface-raised)", borderColor: brandPalette.primary }}
                  >
                    Open file
                  </Button>
                  <Button
                    size="sm"
                    borderRadius="12px"
                    bg={brandPalette.primary}
                    color="white"
                    fontWeight="800"
                    _hover={{ bg: brandPalette.accentDark }}
                    onClick={() => setAnnotatorOpen(true)}
                  >
                    Annotate PDF
                  </Button>
                  {annotatedFile ? (
                    <Badge colorScheme="green" borderRadius="full" px={3} py={1}>
                      Annotated PDF ready
                    </Badge>
                  ) : null}
                </>
              ) : null}
            </HStack>
          </HStack>

          <Box w="100%">
            <FormLabel fontWeight="800" color="var(--text-primary)">
              Evaluation
            </FormLabel>
            <HStack spacing={2} flexWrap="wrap" mb={3}>
              {HOMEWORK_GRADES.map((grade) => {
                const isSelected = selectedGrade === grade;
                return (
                  <Button
                    key={grade}
                    size="sm"
                    borderRadius="full"
                    border="1px solid"
                    borderColor={isSelected ? brandPalette.primary : "var(--border-subtle)"}
                    bg={isSelected ? brandPalette.primary : "var(--surface-muted)"}
                    color={isSelected ? "white" : "var(--text-primary)"}
                    fontWeight="800"
                    onClick={() => applyGrade(grade)}
                    _hover={{
                      bg: isSelected ? brandPalette.accentDark : "var(--surface-raised)",
                      borderColor: brandPalette.primary,
                    }}
                  >
                    {grade}
                  </Button>
                );
              })}
            </HStack>
            <Textarea
              id="comment"
              {...textareaProps}
              value={comment}
              placeholder="Select a grade or write the evaluation…"
              onChange={(e) => setComment(e.target.value)}
              name="homework"
            />
          </Box>

          <Box w="100%">
            <FormLabel fontWeight="800" color="var(--text-primary)">
              Assistant
            </FormLabel>
            <Select
              styles={modalSelectStyles}
              options={assistantsOptions}
              menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              menuPosition="fixed"
              onChange={(e) => {
                setAssistant(e.value);
                setComment((prev) => {
                  const withoutReviewer = prev.replace(/\n\n Reviewed by: .+$/, "").trimEnd();
                  return `${withoutReviewer}\n\n Reviewed by: ${e.sign}`;
                });
              }}
              placeholder="Select assistant"
            />
          </Box>

          <Box w="100%">
            <FormLabel fontWeight="800" color="var(--text-primary)">
              Assistant notes
            </FormLabel>
            <Textarea
              id="assistantComment"
              {...textareaProps}
              placeholder="Write assistant notes…"
              onChange={(e) => setAssistantComment(e.target.value)}
              name="assistantComment"
            />
          </Box>
        </VStack>
      </MainModel>

      <HomeworkPdfAnnotator
        isOpen={annotatorOpen}
        onClose={() => setAnnotatorOpen(false)}
        filename={data?.file}
        onSave={setAnnotatedFile}
      />
    </>
  );
};

export default HomeworkComment;
